import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React from 'react';
import { useEpdProductPanelContent, useGetLCASpecification } from 'services/api/queries';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import {
  columnTotal,
  getBiogenicCarbonWeightHeaderPdf,
  getBiogenicCO2WeightHeaderPdf,
  numericCellBody,
} from '../GridService';
import { DataTableStyled } from '../StyledGrid';
import { useContentDeclarationTerminologyHelper } from '../../hooks/useContentDeclarationTerminologyHelper';

const header = (
  <div className="flex flex-wrap align-items-center justify-content-between gap-2">
    <HeaderCaptionSemiBold className="white-space-nowrap">Panel content</HeaderCaptionSemiBold>
  </div>
);

const PanelContentGridPdf: React.FunctionComponent<{
  epdVersionId: string;
  contentDeclarationType: EPDContentDeclarationProductType;
  isISO21930StandardSelected?: boolean;
  className?: string;
}> = ({ epdVersionId, contentDeclarationType, isISO21930StandardSelected, className }) => {
  const rows = useEpdProductPanelContent(epdVersionId, contentDeclarationType).data;
  const lcaSpecification = useGetLCASpecification(epdVersionId!).data;
  const { replaceInString, replaceInNode } = useContentDeclarationTerminologyHelper();

  if (
    !rows ||
    rows.length === 0 ||
    rows.every((r) => !r.name && !r.weight && !r.weightPercent && !r.biogenicCarbonWeight && !r.biogenicCO2Weight)
  )
    return null;

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column className="p-row-total" footer="Total" />
        <Column className="p-row-total" footer={columnTotal('weight', rows)} />
        <Column className="p-row-total" footer={columnTotal('weightPercent', rows)} />
        <Column className="p-row-total" footer={columnTotal('biogenicCarbonWeight', rows)} />
        {isISO21930StandardSelected && <Column className="p-row-total" footer={columnTotal('biogenicCO2Weight', rows)} />}
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" footer={<div>Note 1</div>} />
        <Column
          className="p-row-note"
          footer={
            <>
              1 kg biogenic carbon is equivalent to 44/12 kg of CO<sub>2</sub>
            </>
          }
          colSpan={isISO21930StandardSelected ? 4 : 3}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTableStyled
      className={className}
      header={header}
      footerColumnGroup={footerGroup}
      value={rows}
      dataKey="id"
      tableStyle={{ minWidth: '49rem' }}
      style={{ overflow: 'auto', display: 'grid' }}
      showGridlines
    >
      <Column field="name" header="Content name" />
      <Column field="weight" header={replaceInString('Weight, kg')} body={numericCellBody()} />
      <Column field="weightPercent" header={replaceInString('Weight-% (versus the product)')} body={numericCellBody()} />
      <Column
        field="biogenicCarbonWeight"
        header={replaceInNode(getBiogenicCarbonWeightHeaderPdf(lcaSpecification?.epdBasedOnUnit))}
        body={numericCellBody()}
      />
      {isISO21930StandardSelected && (
        <Column
          field="biogenicCO2Weight"
          header={replaceInNode(getBiogenicCO2WeightHeaderPdf(lcaSpecification?.epdBasedOnUnit))}
          body={numericCellBody()}
        />
      )}
    </DataTableStyled>
  );
};

export default PanelContentGridPdf;
